import { useFormikEnhanced } from '@superdispatch/forms';
import { useSnackbarStack } from '@superdispatch/ui';
import { parseURITemplate } from '@superdispatch/uri';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlag } from 'shared/settings/FeatureToggles';
import { validateLoadsImportFile } from '../../core/LoadsImportZone';
import { emitLoadEvent } from '../../data/LoadEvents';
import { LoadsImportResult } from '../../data/LoadsAPI';
import { trackLoadsEvent } from '../../data/LoadsAnalytics';
import { useLoadsContext } from '../../data/LoadsContext';
import {
  RejectedImportFile,
  acceptedFilesAtom,
  rejectedFilesAtom,
} from './LoadsImportSnackbar';

interface UseLoadsImportFileInputOptions {
  utmMedium: string;
  onImportSuccess?: () => void;
  onFilesQueued?: () => void;
}

function partitionImportFiles(fileList: FileList | File[]) {
  const acceptedFiles: File[] = [];
  const rejectedFiles: RejectedImportFile[] = [];

  Array.from(fileList).forEach((file) => {
    const validationError = validateLoadsImportFile(file);

    if (validationError) {
      rejectedFiles.push({
        file,
        errors: [validationError],
      });
      return;
    }

    acceptedFiles.push(file);
  });

  return { acceptedFiles, rejectedFiles };
}

export function useLoadsImportFileInput({
  utmMedium,
  onImportSuccess,
  onFilesQueued,
}: UseLoadsImportFileInputOptions) {
  const queryClient = useQueryClient();
  const setFiles = useSetAtom(acceptedFilesAtom);
  const setRejectedFiles = useSetAtom(rejectedFilesAtom);
  const [errorMessage, setErrorMessage] = useState<string>();
  const shouldShowDropzone = useFlag('ctms_loads_dropzone');
  const navigate = useNavigate();
  const { addSnackbar } = useSnackbarStack();
  const { loadsAPI } = useLoadsContext();

  const { values, handleSubmit, isSubmitting, setFieldValue } =
    useFormikEnhanced<{ files: File[] }, LoadsImportResult>({
      initialValues: { files: [] },
      onSubmit: ({ files }) => loadsAPI.uploadLoads(files[0] as File),
      onSubmitSuccess: (data) => {
        if (data.length === 1) {
          navigate(
            parseURITemplate('/loads/{guid}{?next}', {
              guid: data[0]?.guid,
              next: window.location.pathname + window.location.search,
            }),
          );
        } else {
          addSnackbar(`${data.length} loads are successfully imported.`);
        }

        emitLoadEvent('imported');
        onImportSuccess?.();
      },
      onSubmitFailure: ({ message }) => {
        setErrorMessage(message);
      },
    });

  useEffect(() => {
    if (values.files.length && !shouldShowDropzone) {
      handleSubmit();
    }
  }, [handleSubmit, values, values.files.length, shouldShowDropzone]);

  const queueFilesForImport = useCallback(
    (fileList: FileList | File[]) => {
      const { acceptedFiles, rejectedFiles } = partitionImportFiles(fileList);

      if (!shouldShowDropzone) {
        if (acceptedFiles.length) {
          void setFieldValue('files', acceptedFiles);
        }

        return {
          acceptedFiles,
          rejectedFiles,
          didQueue: acceptedFiles.length > 0,
        };
      }

      if (!acceptedFiles.length) {
        return { acceptedFiles, rejectedFiles, didQueue: false };
      }

      queryClient.removeQueries({ queryKey: ['loads-import'] });
      setFiles(acceptedFiles);
      setRejectedFiles(rejectedFiles);
      trackLoadsEvent({
        name: 'Carrier Imported Load',
        utm_medium: utmMedium,
      });
      onFilesQueued?.();

      return { acceptedFiles, rejectedFiles, didQueue: true };
    },
    [
      onFilesQueued,
      queryClient,
      setFieldValue,
      setFiles,
      setRejectedFiles,
      shouldShowDropzone,
      utmMedium,
    ],
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files?.length) {
        return {
          acceptedFiles: [],
          rejectedFiles: [],
          didQueue: false,
        };
      }

      return queueFilesForImport(event.target.files);
    },
    [queueFilesForImport],
  );

  return {
    clearLegacyImportError: () => {
      setErrorMessage(undefined);
    },
    handleFileInputChange,
    inputMultiple: shouldShowDropzone,
    isSubmitting,
    legacyImportErrorMessage: errorMessage,
    legacyImportFiles: values.files,
    queueFilesForImport,
    shouldShowDropzone,
  };
}
